import { useEffect, useState } from 'react';
import { Banner, Button, Card, Dialog, Field, PageHeader, Pill } from '@elysian/ui';
import type { NumberingPatch, NumberSequence, OrganisationSettings } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate } from '../invoices/shared';
import { InboxAddress } from '../bills/InboxAddress';
import { followsOn } from './orgSettings';
import { BUSINESS_KEYS, BusinessFields, formFrom, patchFor, VAT_KEYS, VatFields, YEAR_KEYS, YearFields, type OrgForm } from './OrgFields';

type Section = 'business' | 'vat' | 'year';
const KEYS = { business: BUSINESS_KEYS, vat: VAT_KEYS, year: YEAR_KEYS } as const;
const SAVED = { business: 'Business details saved', vat: 'VAT settings saved', year: 'Financial year saved' } as const;

/**
 * Settings → Organisation: the business, its VAT and its year. Each card saves
 * on its own; the database checks every change (the country is fixed, the
 * currency and year only while nothing is posted, VAT periods follow on).
 */
export function Organisation() {
  const { source, can, load } = useSession();
  const toast = useToast((t) => t.show);
  const data = useAsync(() => source.organisationSettings(), [source]);
  const [s, setS] = useState<OrganisationSettings | null>(null);
  const [f, setF] = useState<OrgForm | null>(null);
  const [busy, setBusy] = useState<Section | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Section, string>>>({});
  const editable = can('settings.accounts');

  useEffect(() => {
    if (data.state === 'ready') { setS(data.data); setF(formFrom(data.data)); }
  }, [data]);

  async function save(section: Section) {
    if (!s || !f) return;
    const patch = patchFor(s, f, KEYS[section]);
    setErrors((e) => ({ ...e, [section]: undefined }));
    if (Object.keys(patch).length === 0) { toast('Nothing has changed'); return; }
    setBusy(section);
    try {
      const next = await source.updateOrganisation(patch);
      setS(next);
      setF((cur) => {
        const fresh = formFrom(next);
        if (!cur) return fresh;
        const keep = { ...cur };
        for (const k of KEYS[section]) (keep as Record<string, unknown>)[k] = fresh[k];
        return keep;
      });
      toast(SAVED[section]);
      void load().catch(() => undefined); // names and VAT settings show elsewhere in the app
    } catch (e) {
      setErrors((x) => ({ ...x, [section]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(null);
    }
  }

  const set = (p: Partial<OrgForm>) => setF((cur) => (cur ? { ...cur, ...p } : cur));
  const dirty = (section: Section) => Boolean(s && f && Object.keys(patchFor(s, f, KEYS[section])).length);
  // A VAT change that would skip or repeat months is explained beside the fields; no point sending it.
  const blocked = (section: Section) => section === 'vat' && Boolean(s && f && f.vat_registered && !followsOn(s.last_vat_return_to, f.vat_frequency, f.vat_period_start_month));
  const footer = (section: Section) => editable && (
    <div className="el-row" style={{ gap: 12, marginTop: 16, alignItems: 'center' }}>
      <Button variant="primary" disabled={busy !== null || !dirty(section) || blocked(section)} onClick={() => void save(section)}>
        {busy === section ? 'Saving…' : 'Save'}
      </Button>
      {dirty(section) && <Button variant="ghost" disabled={busy !== null}
        onClick={() => s && setF((cur) => { const fresh = formFrom(s); const keep = { ...cur! }; for (const k of KEYS[section]) (keep as Record<string, unknown>)[k] = fresh[k]; return keep; })}>Undo changes</Button>}
      {errors[section] && <div className="el-error-box" role="alert" style={{ flex: '1 1 100%' }}>{errors[section]}</div>}
    </div>
  );

  return (
    <>
      <PageHeader title="Organisation" subtitle="The business, its VAT and its financial year. These appear on invoices, returns and exports." />
      {data.state === 'error' && <Banner>Couldn’t load the settings: {data.error.message}</Banner>}
      {data.state === 'loading' && <div className="el-skeleton" style={{ height: 240 }} aria-label="Loading the settings" />}
      {!editable && s && <Banner tone="info">Only the owner, an admin or the accountant can change these.</Banner>}
      {s && f && (
        <div className="el-stack">
          <Card as="section" padding="18px 20px" aria-labelledby="org-business">
            <div className="el-row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <h2 id="org-business" className="el-h2" style={{ margin: 0 }}>The business</h2>
              <span className="el-hint" title={s.locks.jurisdiction ?? undefined}>
                <Pill tone="draft">{s.jurisdiction === 'UK' ? 'United Kingdom' : 'Ireland'}</Pill> Country fixed when the books were set up
              </span>
            </div>
            <div style={{ marginTop: 14 }}><BusinessFields s={s} f={f} set={set} disabled={!editable} /></div>
            {footer('business')}
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="org-vat">
            <h2 id="org-vat" className="el-h2" style={{ marginBottom: 4 }}>VAT</h2>
            <p className="el-hint" style={{ marginTop: 0 }}>
              {s.vat_registered && s.next_vat_period
                ? <>Next return: {s.next_vat_period.label} ({longDate(s.next_vat_period.from)} to {longDate(s.next_vat_period.to)}), due {longDate(s.next_vat_period.due)}.</>
                : 'How and when VAT is worked out and filed.'}
              {s.last_vat_return_to && <> Last return filed to {longDate(s.last_vat_return_to)}; filed returns keep their figures.</>}
            </p>
            <VatFields s={s} f={f} set={set} disabled={!editable} />
            {footer('vat')}
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="org-year">
            <h2 id="org-year" className="el-h2" style={{ marginBottom: 4 }}>Financial year and currency</h2>
            <p className="el-hint" style={{ marginTop: 0 }}>
              {s.locks.year ? 'Later years follow on automatically. Close a year under Year end & periods.' : 'Set these before entering anything: they can’t change once entries are posted.'}
            </p>
            <YearFields s={s} f={f} set={set} disabled={!editable} />
            {footer('year')}
          </Card>

          <ContactCodes s={s} editable={editable} onSaved={(next) => setS(next)} />

          <DocumentNumbers editable={editable} />

          <BillsByEmail editable={editable} />
        </div>
      )}
    </>
  );
}

type Codes = { mode: 'auto' | 'manual'; customer_prefix: string; supplier_prefix: string; digits: number };

/**
 * Short codes for customers and suppliers (#20): typed by hand, or a prefix
 * and a number given automatically. Switching to automatic gives a code to
 * every contact still without one.
 */
function ContactCodes({ s, editable, onSaved }: { s: OrganisationSettings; editable: boolean; onSaved: (next: OrganisationSettings) => void }) {
  const { source } = useSession();
  const toast = useToast((t) => t.show);
  const saved: Codes = {
    mode: s.contact_codes?.mode ?? 'manual', customer_prefix: s.contact_codes?.customer_prefix ?? 'C',
    supplier_prefix: s.contact_codes?.supplier_prefix ?? 'S', digits: s.contact_codes?.digits ?? 4,
  };
  const [c, setC] = useState<Codes>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const without = s.contact_codes?.contacts_without_code ?? 0;
  const dirty = JSON.stringify(c) !== JSON.stringify(saved);
  const prefixOk = (p: string) => /^[A-Z0-9]{0,6}$/.test(p);
  const same = c.mode === 'auto' && c.customer_prefix === c.supplier_prefix;
  const example = (p: string) => `${p}${'1'.padStart(c.digits, '0')}`;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const next = await source.updateOrganisation({ contact_codes: c });
      onSaved(next);
      toast(c.mode === 'auto' && without > 0 && saved.mode !== 'auto'
        ? `Code settings saved; ${without} contact${without === 1 ? '' : 's'} without a code now ${without === 1 ? 'has one' : 'have one'}`
        : 'Code settings saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="org-codes">
      <h2 id="org-codes" className="el-h2" style={{ marginBottom: 4 }}>Customer and supplier codes</h2>
      <p className="el-hint" style={{ marginTop: 0 }}>
        A short code for each customer and supplier, like an account reference in Sage. Invoices and bills from other systems can name a contact by it.
      </p>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={!editable}>
        <legend className="el-label">Codes are</legend>
        <div className="el-row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="el-check"><input type="radio" name="code-mode" checked={c.mode === 'auto'} onChange={() => setC({ ...c, mode: 'auto' })} />Given automatically</label>
          <label className="el-check"><input type="radio" name="code-mode" checked={c.mode === 'manual'} onChange={() => setC({ ...c, mode: 'manual' })} />Typed by hand</label>
        </div>
      </fieldset>
      {c.mode === 'auto' ? (
        <div className="el-form-grid" data-fill="" style={{ marginTop: 12 }}>
          <Field id="code-cust" label="Customer prefix" hint={`First code ${example(c.customer_prefix)}`} error={prefixOk(c.customer_prefix) ? null : 'Up to 6 letters and digits'}>
            <input id="code-cust" className="el-input el-code" maxLength={6} value={c.customer_prefix} disabled={!editable}
              onChange={(e) => setC({ ...c, customer_prefix: e.target.value.toUpperCase().trim() })} />
          </Field>
          <Field id="code-supp" label="Supplier prefix" hint={`First code ${example(c.supplier_prefix)}`}
            error={!prefixOk(c.supplier_prefix) ? 'Up to 6 letters and digits' : same ? 'Different from the customers’ prefix' : null}>
            <input id="code-supp" className="el-input el-code" maxLength={6} value={c.supplier_prefix} disabled={!editable}
              onChange={(e) => setC({ ...c, supplier_prefix: e.target.value.toUpperCase().trim() })} />
          </Field>
          <Field id="code-digits" label="Digits in the number">
            <select id="code-digits" className="el-input" value={c.digits} disabled={!editable} onChange={(e) => setC({ ...c, digits: Number(e.target.value) })}>
              {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        </div>
      ) : (
        <p className="el-hint" style={{ margin: '10px 0 0' }}>Type a code when you add or change a customer or supplier; it can be left blank.</p>
      )}
      {c.mode === 'auto' && saved.mode !== 'auto' && without > 0 && (
        <p className="el-hint" style={{ margin: '10px 0 0' }}>Saving gives a code to the {without} contact{without === 1 ? '' : 's'} without one, oldest first. Codes already given never change.</p>
      )}
      {editable && (
        <div className="el-row" style={{ gap: 12, marginTop: 16, alignItems: 'center' }}>
          <Button variant="primary" disabled={busy || !dirty || same || !prefixOk(c.customer_prefix) || !prefixOk(c.supplier_prefix)} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {dirty && <Button variant="ghost" disabled={busy} onClick={() => setC(saved)}>Undo changes</Button>}
          {error && <div className="el-error-box" role="alert" style={{ flex: '1 1 100%' }}>{error}</div>}
        </div>
      )}
    </Card>
  );
}

type NumberRow = { prefix: string; next_value: string; digits: number };
const numberLabel = (prefix: string, value: number, digits: number) => `${prefix}${String(value).padStart(digits, '0')}`;

/**
 * Document numbers (#13): the prefix, length and next number for each kind of
 * document, so the first invoice here can follow on from Sage's last. Numbers
 * only go up, and never repeat one already used with the same prefix.
 */
function DocumentNumbers({ editable }: { editable: boolean }) {
  const { source } = useSession();
  const toast = useToast((t) => t.show);
  const [list, setList] = useState<NumberSequence[] | null>(null);
  const [rows, setRows] = useState<Record<string, NumberRow>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = (l: NumberSequence[]) => {
    setList(l);
    setRows(Object.fromEntries(l.map((n) => [n.key, { prefix: n.prefix, next_value: String(n.next_value), digits: n.digits }])));
  };
  useEffect(() => {
    let live = true;
    source.numbering().then((l) => live && reset(l), (e: unknown) => live && setLoadError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [source]);

  if (!list) {
    return (
      <Card as="section" padding="18px 20px" aria-labelledby="org-numbers">
        <h2 id="org-numbers" className="el-h2" style={{ marginBottom: 4 }}>Document numbers</h2>
        {loadError ? <div className="el-error-box" role="alert">{loadError}</div> : <p className="el-hint">Loading…</p>}
      </Card>
    );
  }

  const problem = (n: NumberSequence): string | null => {
    const r = rows[n.key]!;
    if (!/^[A-Za-z0-9/_.-]{0,10}$/.test(r.prefix)) return 'A prefix is up to 10 letters, digits and - / _ .';
    if (!/^\d{1,10}$/.test(r.next_value) || Number(r.next_value) < 1) return 'Enter a whole number, 1 or more';
    if (Number(r.next_value) < n.next_value && r.prefix === n.prefix) return `Can’t go back: the next is already ${n.next}`;
    if (r.prefix === n.prefix && n.last_used !== null && n.last_used !== undefined && Number(r.next_value) <= n.last_used) {
      return `${numberLabel(n.prefix, n.last_used, r.digits)} has been used; start after it`;
    }
    return null;
  };
  const changed = list.filter((n) => {
    const r = rows[n.key]!;
    return r.prefix !== n.prefix || r.next_value !== String(n.next_value) || r.digits !== n.digits;
  });
  const blocked = list.some((n) => problem(n));
  const set = (key: string, patch: Partial<NumberRow>) => setRows((all) => ({ ...all, [key]: { ...all[key]!, ...patch } }));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const patch: NumberingPatch = {};
      for (const n of changed) {
        const r = rows[n.key]!;
        patch[n.key] = {
          ...(r.prefix !== n.prefix ? { prefix: r.prefix } : {}),
          ...(r.next_value !== String(n.next_value) ? { next_value: Number(r.next_value) } : {}),
          ...(r.digits !== n.digits ? { digits: r.digits } : {}),
        };
      }
      reset(await source.setNumbering(patch));
      toast('Document numbers saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="org-numbers">
      <h2 id="org-numbers" className="el-h2" style={{ marginBottom: 4 }}>Document numbers</h2>
      <p className="el-hint" style={{ marginTop: 0 }}>
        How invoices, credit notes, bills and journals are numbered. Coming from Sage? Set the next number to one after your last there, so the numbers carry on. A change applies from the next document.
      </p>
      <div className="el-numbering" role="table" aria-label="Document numbers">
        <div className="el-numbering-row el-numbering-head" role="row">
          <span role="columnheader">Document</span><span role="columnheader">Prefix</span><span role="columnheader">Next number</span>
          <span role="columnheader">Digits</span><span role="columnheader">Next will be</span>
        </div>
        {list.map((n) => {
          const r = rows[n.key]!;
          const err = problem(n);
          const preview = /^\d{1,10}$/.test(r.next_value) ? numberLabel(r.prefix, Number(r.next_value), r.digits) : '—';
          return (
            <div key={n.key} className="el-numbering-row" role="row">
              <span role="rowheader" className="el-numbering-kind">
                <strong>{n.label}</strong>
                <span className="el-hint">{n.last_used !== null && n.last_used !== undefined ? `Last used ${numberLabel(n.prefix, n.last_used, n.digits)}` : 'None yet'}</span>
              </span>
              <span role="cell" data-label="Prefix">
                <input aria-label={`${n.label} prefix`} className="el-input el-code" maxLength={10} value={r.prefix} disabled={!editable}
                  onChange={(e) => set(n.key, { prefix: e.target.value.trim() })} />
              </span>
              <span role="cell" data-label="Next number">
                <input aria-label={`${n.label} next number`} className="el-input" inputMode="numeric" value={r.next_value} disabled={!editable}
                  aria-invalid={err ? true : undefined} onChange={(e) => set(n.key, { next_value: e.target.value.replace(/\D/g, '') })} />
              </span>
              <span role="cell" data-label="Digits">
                <select aria-label={`${n.label} digits`} className="el-input" value={r.digits} disabled={!editable} onChange={(e) => set(n.key, { digits: Number(e.target.value) })}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => <option key={d} value={d}>{d === 1 ? 'Any' : d}</option>)}
                </select>
              </span>
              <span role="cell" className="el-numbering-next"><span className="el-code">{preview}</span></span>
              {err && <span className="el-numbering-error" role="alert">{n.label}: {err}</span>}
            </div>
          );
        })}
      </div>
      {editable && (
        <div className="el-row" style={{ gap: 12, marginTop: 16, alignItems: 'center' }}>
          <Button variant="primary" disabled={busy || changed.length === 0 || blocked} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
          {changed.length > 0 && <Button variant="ghost" disabled={busy} onClick={() => reset(list)}>Undo changes</Button>}
          {error && <div className="el-error-box" role="alert" style={{ flex: '1 1 100%' }}>{error}</div>}
        </div>
      )}
    </Card>
  );
}

/**
 * Bills by email (#22): the books' own address, read from the platform's mailbox.
 * A new address replaces the old one, which then stops working (for spam).
 */
function BillsByEmail({ editable }: { editable: boolean }) {
  const { source } = useSession();
  const toast = useToast((t) => t.show);
  const [version, setVersion] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function replace() {
    setBusy(true);
    try {
      const next = await source.newInboxAddress();
      toast(`New address: ${next.address}. The old one no longer works.`);
      setVersion((v) => v + 1);
      setConfirming(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="org-bills-email">
      <h2 id="org-bills-email" className="el-h2" style={{ marginBottom: 4 }}>Bills by email</h2>
      <p className="el-hint" style={{ marginTop: 0 }}>
        Suppliers can send bills to this address, or you can forward them to it. PDFs and photos attached arrive in the bill inbox within a few minutes, checked for viruses and read for you.
      </p>
      <div className="el-row" style={{ alignItems: 'flex-end', gap: 16 }}>
        <InboxAddress version={version} label="The books’ address" />
        {editable && <div style={{ marginBottom: 16 }}><Button variant="ghost" onClick={() => setConfirming(true)}>New address</Button></div>}
      </div>
      {confirming && (
        <Dialog title="Make a new address?" onClose={() => setConfirming(false)}
          actions={<>
            <Button variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="danger" disabled={busy} onClick={() => void replace()}>Make a new address</Button>
          </>}>
          <p style={{ margin: 0 }}>Bills sent to the current address will stop arriving. Use this if it’s getting spam, then give the new one to your suppliers.</p>
        </Dialog>
      )}
    </Card>
  );
}
